// pt-BR of Routing Intelligence v2 (decision D24); complete for every key of en-routing.ts (checked by the type).
import type { enRouting } from "./en-routing.ts";

export const ptBRRouting: Record<keyof typeof enRouting, string> = {
  "ri.unknown": "desconhecido",

  // Model tiers in the routing configuration editor (D29)
  "ri.tiers.title": "Routing Intelligence — camadas de modelos",
  "ri.tiers.hint": "Quais modelos as novas tentativas de construção podem usar em cada camada. Fica salvo no rascunho; nada muda até a versão ser ativada.",
  "ri.tiers.enabled": "Routing Intelligence ativado",
  "ri.tiers.enabledHint": "Desligado por padrão. Quando ligado, as novas tentativas de construção são roteadas pelas camadas econômica e premium abaixo.",
  "ri.tiers.economy": "Modelos econômicos",
  "ri.tiers.economyHint": "Tentados primeiro, nesta ordem. Modelos gratuitos são permitidos.",
  "ri.tiers.premium": "Modelos premium",
  "ri.tiers.premiumHint": "Usados quando a tarefa exige mais. Vazio significa o modelo do Maestro e seus substitutos.",
  "ri.tiers.count": "{n} de {max} selecionados",
  "ri.tiers.full": "Limite de {max} atingido: remova um para adicionar outro.",
  "ri.tiers.noTargets": "Nenhum modelo de chat cadastrado ainda. Adicione um em Configurações → Modelos e combos.",
  "ri.tiers.via": "via {connection}",
  "ri.tiers.notVerified": "({state})",
  "ri.tiers.unknownTarget": "(modelo desconhecido)",
  "ri.tiers.unverifiedWarning": "O roteamento ignora modelos não verificados até que sejam verificados.",

  // Vocabulary
  "ri.tier.deterministic": "Determinístico",
  "ri.tier.economy": "Econômico",
  "ri.tier.premium": "Premium",
  "ri.source.rules": "Regras",
  "ri.source.jev": "Jev",
  "ri.source.jev_rules": "Jev combinado com regras",
  "ri.source.conservative": "Confiança baixa, conservador",
  "ri.source.degraded": "Degradado — regras",
  "ri.cache.unknown": "Estado do cache desconhecido",
  "ri.cache.cold": "Cache frio",
  "ri.cache.warming": "Cache aquecendo",
  "ri.cache.warm": "Cache quente",
  "ri.cache.lost": "Cache perdido",
  "ri.affinity.openrouter_session_id": "Sessão do OpenRouter",
  "ri.affinity.unsupported": "Não suportada neste caminho",
  "ri.affinity.disabled": "Desligada",
  "ri.escalation.quality_gate_failed": "uma verificação de qualidade falhou",
  "ri.escalation.critic_rejected": "o crítico rejeitou o trabalho",
  "ri.escalation.repeated_test_failure": "os testes falharam repetidamente",
  "ri.escalation.no_progress": "não houve progresso",
  "ri.escalation.context_capacity": "o contexto não cabe mais",
  "ri.escalation.capability_missing": "falta uma capacidade necessária",
  "ri.escalation.provider_unavailable": "o provedor está indisponível",
  "ri.escalation.quota_exhausted": "a cota acabou",
  "ri.escalation.high_risk_policy": "a política de alto risco",
  "ri.escalation.human_override": "a decisão de uma pessoa",
  "ri.domain.frontend": "Frontend",
  "ri.domain.backend": "Backend",
  "ri.domain.database": "Banco de dados",
  "ri.domain.devops": "DevOps",
  "ri.domain.security": "Segurança",
  "ri.domain.tests": "Testes",
  "ri.domain.documentation": "Documentação",
  "ri.domain.research": "Pesquisa",
  "ri.domain.integration": "Integração",
  "ri.domain.mixed": "Misto",
  "ri.kind.investigation": "Investigação",
  "ri.kind.implementation": "Implementação",
  "ri.kind.refactor": "Refatoração",
  "ri.kind.bugfix": "Correção de bug",
  "ri.kind.test": "Teste",
  "ri.kind.documentation": "Documentação",
  "ri.kind.review": "Revisão",
  "ri.kind.operation": "Operação",
  "ri.complexity.trivial": "Trivial",
  "ri.complexity.low": "Baixa",
  "ri.complexity.medium": "Média",
  "ri.complexity.high": "Alta",
  "ri.complexity.critical": "Crítica",

  // Routing decision (task detail)
  "ri.decision.title": "Decisão de roteamento",
  "ri.decision.loadFailed": "Não foi possível carregar as decisões de roteamento",
  "ri.decision.none": "Ainda não há decisão de roteamento. Ela é registrada quando uma tentativa desta tarefa começa.",
  "ri.decision.classifier": "Classificador",
  "ri.decision.confidence": "Confiança",
  "ri.decision.class": "Classe",
  "ri.decision.agent": "Agente",
  "ri.decision.agentVersion": "versão {id}",
  "ri.decision.tier": "Nível",
  "ri.decision.requestedTier": "pedido: {tier}",
  "ri.decision.model": "Modelo",
  "ri.decision.provider": "Provedor",
  "ri.decision.cache": "Cache",
  "ri.decision.hit": "{pct} de acerto",
  "ri.decision.hitUnknown": "taxa de acerto desconhecida",
  "ri.decision.affinity": "Afinidade de sessão",
  "ri.decision.reason": "Motivo",
  "ri.decision.premiumReview": "Revisão premium obrigatória",
  "ri.decision.escalated": "Escalado porque {reason}",
  "ri.decision.rejected": "Candidatos rejeitados ({n})",
  "ri.decision.noRejected": "Nenhum candidato foi rejeitado.",
  "ri.decision.history": "Decisões anteriores",
  "ri.decision.decidedAt": "Decidido {when}",

  // Model performance (Agents)
  "ri.perf.tab": "Desempenho dos modelos",
  "ri.perf.subtitle": "Qual modelo entrega trabalho aceito por classe de tarefa, a que custo e com quanto cache.",
  "ri.perf.groupBy": "Agrupar por",
  "ri.perf.group.task_class,model,provider": "Classe de tarefa × modelo × provedor",
  "ri.perf.group.model,provider": "Modelo × provedor",
  "ri.perf.group.task_class,tier": "Classe de tarefa × nível",
  "ri.perf.group.agent_version,model": "Versão do agente × modelo",
  "ri.perf.group.tier": "Nível",
  "ri.perf.key.task_class": "Classe de tarefa",
  "ri.perf.key.specialty": "Especialidade",
  "ri.perf.key.risk": "Risco",
  "ri.perf.key.complexity": "Complexidade",
  "ri.perf.key.agent_version": "Versão do agente",
  "ri.perf.key.model": "Modelo",
  "ri.perf.key.provider": "Provedor",
  "ri.perf.key.reasoning_effort": "Esforço de raciocínio",
  "ri.perf.key.tier": "Nível",
  "ri.perf.since": "Desde {when}",
  "ri.perf.table": "Resultados por grupo",
  "ri.perf.col.tasks": "Tarefas",
  "ri.perf.col.attempts": "Tentativas",
  "ri.perf.col.accepted": "Aceitas",
  "ri.perf.col.firstPass": "De primeira",
  "ri.perf.col.costPerAccepted": "Custo por tarefa aceita",
  "ri.perf.col.cacheHit": "Acerto de cache",
  "ri.perf.col.premiumEscalation": "Escalada para premium",
  "ri.perf.col.medianDuration": "Duração mediana",
  "ri.perf.col.infra": "Interrupções de infraestrutura",
  "ri.perf.partialCost": "parcial: custo desconhecido em {n} tentativas",
  "ri.perf.infraHint": "Interrupções de infraestrutura (espera de cota e queda de provedor) não contam como falha.",
  "ri.perf.configExcluded": "{count} tentativa(s) abortada(s) por divergência de modelo (rota que respondeu com um modelo não autorizado) são erro de configuração, não qualidade do modelo, e ficam fora desta tabela.",
  "ri.perf.emptyTitle": "Nenhuma tentativa roteada neste período",
  "ri.perf.emptyBody": "Os resultados aparecem depois que tentativas forem roteadas pelo Routing Intelligence v2.",
  "ri.perf.maintainerOnly": "Só mantenedores e administradores veem o desempenho dos modelos da organização.",
  "ri.promo.title": "Econômico vs premium: promoção",
  "ri.promo.hint": "Um veredito é uma hipótese a calibrar, não uma garantia estatística.",
  "ri.promo.criteria": "Critérios: pelo menos {min} avaliações, diferença de sucesso de até {gap} p.p., custo de até {cost}× e tempo de ciclo de até {cycle}× o do modelo premium.",
  "ri.promo.none": "Nenhum modelo econômico foi comparado a um modelo premium ainda.",
  "ri.promo.col.class": "Classe de tarefa",
  "ri.promo.col.models": "Modelo econômico vs premium",
  "ri.promo.vs": "vs {model}",
  "ri.promo.col.evaluations": "Avaliações (econômico · premium)",
  "ri.promo.col.gap": "Diferença de sucesso",
  "ri.promo.col.costRatio": "Razão de custo",
  "ri.promo.col.cycleRatio": "Razão de ciclo",
  "ri.promo.col.verdict": "Veredito",
  "ri.promo.verdict.promote": "Promover",
  "ri.promo.verdict.hold": "Manter",
  "ri.promo.verdict.insufficient_data": "Dados insuficientes",
  "ri.promo.pp": "{n} p.p.",

  // Prompt cache (Observability)
  "ri.cache.title": "Cache de prompt",
  "ri.cache.hint": "Toda a organização, a partir das chamadas de modelo das tentativas roteadas.",
  "ri.cache.freshInput": "Tokens de entrada novos",
  "ri.cache.reads": "Leituras do cache",
  "ri.cache.writes": "Escritas no cache",
  "ri.cache.hitRatio": "Taxa de acerto",
  "ri.cache.savings": "Economia estimada",
  "ri.cache.savingsPartial": "parcial: preço conhecido em {known} de {total} tentativas",
  "ri.cache.coldStarts": "Partidas a frio",
  "ri.cache.providerChanges": "Trocas de provedor",
  "ri.cache.prefixChanges": "Trocas de prefixo",
  "ri.cache.byState": "Tentativas por estado do cache",
  "ri.cache.noAttempts": "Nenhuma tentativa roteada neste período.",

  // Jev classifier (Observability)
  "ri.jev.title": "Classificador Jev",
  "ri.jev.model": "Modelo",
  "ri.jev.policy": "Versão da política",
  "ri.jev.routingV2": "Routing Intelligence v2",
  "ri.jev.jevOpenrouter": "Jev pelo OpenRouter",
  "ri.jev.on": "Ligado",
  "ri.jev.off": "Desligado",
  "ri.jev.connection": "Conexão",
  "ri.jev.noConnection": "Nenhuma conexão configurada",
  "ri.jev.realCalls": "Chamadas reais",
  "ri.jev.realAllowed": "Permitidas neste servidor",
  "ri.jev.realNotAllowed": "Não permitidas neste servidor",
  "ri.jev.readiness": "Prontidão",
  "ri.jev.ready": "Pronto",
  "ri.jev.notReady": "Não está pronto",
  "ri.jev.thresholds": "Limiares",
  "ri.jev.thresholdsValue": "aplica o Jev a partir de {auto} · combina com regras a partir de {combine}",
  "ri.jev.impediments": "Impedimentos",
  "ri.jev.windowSince": "Desde {when}",
  "ri.jev.countJev": "Classificações do Jev",
  "ri.jev.countRules": "Só regras",
  "ri.jev.countDegraded": "Degradadas",
  "ri.jev.avgLatency": "Latência média",
  "ri.jev.cost": "Custo",
  "ri.jev.lastDegraded": "Última degradação",
  "ri.jev.neverDegraded": "Nenhuma neste período",
  "ri.jev.calibration": "Calibração: confiança × resultado real",
  "ri.jev.bin": "Confiança",
  "ri.jev.bin.lt_0_70": "abaixo de 70%",
  "ri.jev.bin.0_70_to_0_90": "de 70% a 90%",
  "ri.jev.bin.gte_0_90": "90% ou mais",
  "ri.jev.classifications": "Classificações",
  "ri.jev.accepted": "Aceitas",
  "ri.jev.failed": "Falharam",
  "ri.jev.noCalibration": "Nenhuma tentativa classificada terminou ainda.",
  "ri.jev.maintainerOnly": "Só mantenedores e administradores veem o classificador Jev.",

  // Jev paid test
  "ri.test.title": "Testar classificação",
  "ri.test.paidTitle": "Isto faz uma chamada real e paga",
  "ri.test.paidBody": "A tarefa abaixo é enviada ao Jev pelo OpenRouter e a chamada é cobrada (normalmente uma fração de centavo). Defina o máximo que ela pode custar.",
  "ri.test.realCallsOff": "Este servidor não permite chamadas reais para a conexão; o teste será recusado.",
  "ri.test.taskTitle": "Título da tarefa",
  "ri.test.description": "Descrição",
  "ri.test.type": "Tipo",
  "ri.test.risk": "Risco",
  "ri.test.maxCost": "Custo máximo (centavos)",
  "ri.test.maxCostHint": "Obrigatório. Acima de 0 e até 100 centavos.",
  "ri.test.titleRequired": "Informe um título para a tarefa.",
  "ri.test.maxCostInvalid": "Informe um custo acima de 0 e até 100 centavos.",
  "ri.test.submit": "Fazer teste pago",
  "ri.test.notAllowedTitle": "Chamadas reais não estão habilitadas neste servidor",
  "ri.test.notAllowedBody": "O servidor recusou o teste antes de chamar o provedor. Um operador precisa permitir chamadas reais para esta conexão antes.",
  "ri.test.failed": "A classificação falhou",
  "ri.test.result": "Resultado",
  "ri.test.dimensions": "Dimensões",
  "ri.test.effective": "O que o MaestrOS aplicaria",
  "ri.test.source": "Origem",
};
